/**
 * CeoReportView — the CEO AI report, rendered.
 *
 * Card P201 (P197 PR2). Daniel's complaint on 31 Oct was that the report
 * "reads as plain text". STEP 0 made the markdown render properly; this
 * replaces the markdown with structure.
 *
 * ⛔⛔ THE AI WRITES THE WORDS. THE NUMBERS AND CHARTS COME FROM THE PAYLOAD,
 * DRAWN BY OUR CODE. Nothing on this page is a figure the model typed:
 *   · the KPI strip reads payload.metrics — the dashboard's own metric
 *     objects, so a tile here cannot disagree with the same tile there;
 *   · every chart reads payload at the metricKey the server already
 *     validated, through the SAME dotted-path rule the server used;
 *   · the model's bullets are prose, rendered as text.
 * The model can therefore be wrong about an interpretation. It cannot be
 * wrong about a number, because it never supplies one to this file.
 *
 * ⚠ CHART_REGISTRY's keys MUST equal the server's CHART_TYPES exactly — a
 * type on one side and not the other is a chart that validates and never
 * draws, or draws unvalidated. ceoReportSchema.test.js asserts set equality
 * rather than trusting this comment.
 *
 * ⚠ Class names only, no styling of the soft-UI surfaces: `ceo-ai-report` on
 * the dialog surface (in AiPipelineReport.js), `ceo-ai-report-body` on this
 * article, and `ceo-chart-well` on each chart surface are W2's hooks
 * (Phase 2). They are deliberately empty here — W2 styles them in
 * ceoSoftUi.css, and a local rule would be the thing their stylesheet has to
 * fight.
 *
 * ⚠ Colours come from _overviewTheme (C/S), never a literal hex, so this
 * inherits the soft-UI retune and the AA-corrected tokens. The two
 * exceptions are declared at RISK_DOT and the status pills, which build
 * tints from those same tokens.
 */

import React, { useMemo } from 'react';
import { C, S, FONT, deltaTone } from './_overviewTheme';
import {
  captionForReason, formatRangeLabel, formatGeneratedAt, stripStrayMarkdown,
} from './_reportFormat';
import {
  RevenueChart, MovementWaterfall, SalesFunnel,
  BarByRep, BarByStage, ReceivablesAging, SourceDonut,
} from './CeoCharts';

/* ── Formatting ───────────────────────────────────────────────────────────
 * ⚠ ONE FORMATTER PER UNIT, chosen by the metric's OWN declared unit. The
 * repo has a standing scar here: `unit: 'currency'` meant cents in one module
 * and dollars in another, a 100x difference with nothing on the wire to tell
 * them apart, and _ceo_metric.js now throws on it. So this never guesses from
 * a field name.
 */
export function formatMetric(m) {
  if (!m || m.value === null || m.value === undefined) return '—';
  const v = Number(m.value);
  if (!Number.isFinite(v)) return '—';
  switch (m.unit) {
    case 'cents':   return compactMoney(v / 100);
    case 'dollars': return compactMoney(v);
    case 'percent': return `${Math.round(v * 10) / 10}%`;
    case 'days':    return `${Math.round(v * 10) / 10}d`;
    default:        return v.toLocaleString();
  }
}

/** $1.44M / $12.3K / $940 — the card asked for compact money. */
export function compactMoney(dollars) {
  const n = Number(dollars) || 0;
  const sign = n < 0 ? '-' : '';
  const a = Math.abs(n);
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e4) return `${sign}$${(a / 1e3).toFixed(1)}K`;
  return `${sign}$${Math.round(a).toLocaleString()}`;
}

export function formatDelta(m) {
  if (!m || m.deltaPct === null || m.deltaPct === undefined) return null;
  const p = Number(m.deltaPct);
  if (!Number.isFinite(p) || p === 0) return null;
  return `${p > 0 ? '▲' : '▼'} ${Math.abs(p)}%`;
}

/* ── The KPI strip ────────────────────────────────────────────────────────
 * ⛔⛔ DIRECTION IS A PROPERTY OF THE TILE, NEVER OF THE ARITHMETIC. Collected
 * revenue rising is good; unpaid receivables rising is bad. A strip that
 * painted every "up" green would be confidently wrong on half of these, so
 * each tile declares higherIsBetter and deltaTone() is given it.
 */
const KPI_TILES = Object.freeze([
  { key: 'openValue',      label: 'Open pipeline', higherIsBetter: true },
  { key: 'bookedWonValue', label: 'Won',           higherIsBetter: true },
  { key: 'collectedValue', label: 'Collected',     higherIsBetter: true },
  { key: 'leadsInRange',   label: 'New leads',     higherIsBetter: true },
  { key: 'leadToDeposit',  label: 'Lead → deposit', higherIsBetter: true },
]);

export function kpiTiles(payload) {
  const m = (payload && payload.metrics) || null;
  const out = [];
  if (m) {
    for (const t of KPI_TILES) {
      const metric = m[t.key];
      /* ⛔ A metric the dashboard could not compute is DROPPED, not rendered
         as 0. `tracked: false` means the system could not see it, and a zero
         in a KPI tile is read as a fact about the business. */
      if (metric && metric.tracked) out.push({ ...t, metric });
    }
  }
  /*
   * ⚠ Receivables has NO previous window — unpaidByAge() is a snapshot, not a
   * windowed query. It is shown with no arrow and a stated reason rather than
   * with a fabricated one. Up is BAD here.
   */
  const rec = payload && payload.receivables;
  const recData = rec && rec.tracked ? rec.data : null;
  if (recData && typeof recData.cents === 'number') {
    out.push({
      key: 'receivables', label: 'Unpaid invoices', higherIsBetter: false,
      metric: {
        value: recData.cents, previous: null, delta: null, deltaPct: null,
        unit: 'cents', tracked: true,
        /* ⚠ A CODE, not a sentence. This tile is the one place the client
           mints a metric of its own, and in P201 I wrote prose into the field
           every other metric fills with an identifier — so it rendered
           verbatim beside two leaked codes. Same vocabulary as the server. */
        previousReason: 'snapshot_of_now_has_no_prior_window',
      },
    });
  }
  return out.slice(0, 6);
}

/* ── The chart registry ───────────────────────────────────────────────────
 * ⚠ Keys MUST equal the server's CHART_TYPES. Asserted by test, not assumed.
 */
export const CHART_REGISTRY = Object.freeze({
  funnel:       (d, p) => <SalesFunnel funnel={d} unmappedStages={p && p.charts && p.charts.tracked ? p.charts.data.unmappedStages : null}
                                       winRate={p && p.charts && p.charts.tracked ? p.charts.data.winRate : null}
                                       terminalBuckets={(p && p.charts && p.charts.tracked && p.charts.data.terminalBuckets) || []} compact />,
  bar_by_rep:   (d) => <BarByRep data={d} compact />,
  bar_by_stage: (d) => <BarByStage data={d} compact />,
  trend:        (d) => <RevenueChart data={(d && d.tracked ? d.data : d) || []} />,
  waterfall:    (d) => <MovementWaterfall movement={d} />,
  aging:        (d) => <ReceivablesAging data={d} />,
  donut:        (d) => <SourceDonut data={d} />,
});

/**
 * Resolve a dotted path. ⛔⛔ THE SAME RULE THE SERVER VALIDATED WITH
 * (routes/_ceo_report_schema.js resolvePath). It is duplicated rather than
 * imported because create-react-app's module scope forbids src/ importing
 * routes/ — which is exactly why the two sides agree on a GENERIC path rule
 * instead of on a table of aliases. There is no mapping here to drift; a test
 * feeds both implementations the same cases.
 */
export function resolvePath(root, path) {
  if (!root || typeof path !== 'string' || path.length === 0) return undefined;
  if (!/^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z_][A-Za-z0-9_]*)*$/.test(path)) return undefined;
  let node = root;
  for (const part of path.split('.')) {
    if (node === null || typeof node !== 'object' || !Object.prototype.hasOwnProperty.call(node, part)) {
      return undefined;
    }
    node = node[part];
  }
  return node;
}

/* ── Inline emphasis in a bullet ──────────────────────────────────────────
 * The prompt asks for figures in **bold**. ⛔ This renders React elements, so
 * the model's text is escaped by React — there is no HTML path here and must
 * never be one.
 */
function emphasise(text) {
  const src = String(text == null ? '' : text);
  const parts = [];
  const re = /\*\*(.+?)\*\*/g;
  let last = 0, m, k = 0;
  while ((m = re.exec(src)) !== null) {
    if (m.index > last) parts.push(stripStrayMarkdown(src.slice(last, m.index)));
    /* ⛔ The bold run itself is stripped too: ***both*** leaves a stray star
       inside the pair, which would then print inside the <strong>. */
    parts.push(<strong key={k++} style={{ color: C.text }}>{stripStrayMarkdown(m[1])}</strong>);
    last = re.lastIndex;
  }
  if (last < src.length) parts.push(stripStrayMarkdown(src.slice(last)));
  return parts.length ? parts : stripStrayMarkdown(src);
}

const PILL = {
  good:  { bg: '#ECFDF5', fg: '#15803D', label: 'Good' },
  watch: { bg: '#FFFBEB', fg: C.watch,   label: 'Watch' },
  act:   { bg: C.highBg,  fg: C.high,    label: 'Act' },
};

function StatusPill({ status }) {
  /* ⛔ No pill at all when the model did not give a verdict. A default "Good"
     would be the model's silence rendered as reassurance. */
  if (!status || !PILL[status]) return null;
  const p = PILL[status];
  return (
    <span data-testid={`status-${status}`}
          style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase',
                   padding: '2px 8px', borderRadius: 999, background: p.bg, color: p.fg }}>
      {p.label}
    </span>
  );
}

const RISK_DOT = { high: C.high, medium: C.watch, low: C.muted };

export default function CeoReportView({
  report, payload, droppedCharts = [], period = null, pipelineLabel = 'All pipelines',
  /*
   * ⚠ Card P224 — the PDF rasterises the charts the reader is LOOKING AT, so
   * it needs a handle on the rendered tree. A ref rather than a document-wide
   * querySelector, because two report views could exist (the dialog and any
   * future embed) and a selector would capture whichever came first.
   */
  rootRef = null,
}) {
  const tiles = useMemo(() => kpiTiles(payload), [payload]);
  if (!report) return null;

  const range = (payload && payload.period) || period || null;
  const rangeLabel = formatRangeLabel(range);
  const generated = payload && payload.generated_at;

  return (
    <article
      ref={rootRef}
      className="ceo-ai-report-body"
      data-testid="ceo-report-view"
      /* ⚠ ~70ch, the card's readability ask. A ch unit tracks the font, so it
         stays ~70 CHARACTERS rather than a pixel width that drifts with it. */
      style={{ fontFamily: FONT, color: C.body, maxWidth: '72ch', margin: '0 auto', minWidth: 0 }}
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header style={{ marginBottom: 14, paddingBottom: 10, borderBottom: `1px solid ${C.rule}` }}>
        <h2 style={{ ...S.h2, fontSize: 15 }}>Pipeline brief</h2>
        <div style={{ fontSize: 11, color: C.faint, marginTop: 4, lineHeight: 1.6 }}>
          <span data-testid="report-pipeline">{pipelineLabel}</span>
          {/* ⚠ DATES, NOT A NAME. The server labels any explicit from/to
              "Custom range" whatever the user picked, so the brief said
              "Custom range" while the dialog said "Month" — two labels, one
              window, and neither naming a day. */}
          {rangeLabel ? (
            <> · <span data-testid="report-range">{rangeLabel}</span></>
          ) : null}
          {generated ? <> · generated {formatGeneratedAt(generated)}</> : null}
        </div>
      </header>

      {/* ── Headline + summary, in large type ──────────────────────────── */}
      {report.headline ? (
        <p data-testid="report-headline"
           style={{ fontSize: 19, lineHeight: 1.35, fontWeight: 700, color: C.text, margin: '0 0 8px' }}>
          {/* ⛔⛔ THE BULLETS WERE EMPHASISED AND THESE TWO WERE NOT — which is
              the whole of the v2.1.54 defect. The prompt asks for figures in
              bold, so the model obliged and the headline printed its own
              asterisks. Same emphasiser, same stray-star strip, one function. */}
          {emphasise(report.headline)}
        </p>
      ) : null}
      {report.summary ? (
        <p data-testid="report-summary" style={{ fontSize: 14, lineHeight: 1.6, margin: '0 0 16px' }}>
          {emphasise(report.summary)}
        </p>
      ) : null}

      {/* ── KPI strip ──────────────────────────────────────────────────── */}
      {tiles.length ? (
        <div data-testid="report-kpis"
             style={{ display: 'grid', gap: 10, marginBottom: 18,
                      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
          {tiles.map((t) => {
            const d = formatDelta(t.metric);
            return (
              <div key={t.key} className="ceo-chart-well" data-testid={`kpi-${t.key}`}
                   style={{ ...S.card, padding: 12 }}>
                <div style={{ ...S.kpiLabel, fontSize: 11 }}>{t.label}</div>
                <div style={{ ...S.kpiValue, fontSize: 22 }}>{formatMetric(t.metric)}</div>
                {d ? (
                  <div data-testid={`kpi-delta-${t.key}`}
                       style={{ fontSize: 11, marginTop: 4, fontWeight: 600,
                                color: deltaTone(t.metric.deltaPct, t.higherIsBetter) }}>
                    {d}
                  </div>
                ) : (
                  /* ⚠ SAYS WHY THERE IS NO ARROW. "No prior window" and "flat"
                     are different facts and a blank space conflates them. */
                  /* ⛔⛔ A MAPPED CAPTION OR NOTHING — never the code. Three
                     tiles printed internal identifiers from _ceo_metric.js,
                     truncated mid-word by the tile's own width. */
                  <div style={{ ...S.kpiSub, fontSize: 10 }}>
                    {captionForReason(t.metric.previousReason) || 'No change on the previous period'}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : null}

      {/*
        * ⛔⛔ CARD P227 D — THE TILES AND THE BRIEF CAN COVER DIFFERENT
        * SCOPES, AND THE PAGE NOW SAYS SO.
        *
        * Daniel's first real PDF was headed "Patient Acquisition 2.0" with
        * $509,333 open, above a KPI strip reading $2.21M and 16 new leads —
        * the ALL-PIPELINES figures, unlabelled. Both numbers are correct and
        * the reader has no way to know they answer different questions.
        *
        * ⚠ The payload has always said this: buildCeoDashboard has no
        * pipeline dimension, so `headline_is_scoped` is false in EVERY
        * payload and the system prompt already forbids the model from
        * presenting a headline figure as the pipeline's. The screen was the
        * last place that still implied otherwise.
        *
        * ⚠ A LABEL, NOT A RESCOPE. Scoping the tiles would mean giving the
        * dashboard builder a pipeline dimension it does not have — a real
        * piece of work, and not this card's.
        */}
      {tiles.length && payload && payload.headline_is_scoped === false
        && pipelineLabel && pipelineLabel !== 'All pipelines' ? (
        <div data-testid="report-kpi-scope"
             style={{ fontSize: 11, color: C.watch, lineHeight: 1.5, margin: '-8px 0 16px' }}>
          ⚠ These tiles cover <strong>all pipelines</strong>. The brief below covers{' '}
          <strong>{pipelineLabel}</strong>; its own figures are in the sections.
        </div>
      ) : null}

      {/* ── Sections ───────────────────────────────────────────────────── */}
      {report.sections.map((s, i) => {
        const draw = s.chart && CHART_REGISTRY[s.chart.type];
        const data = s.chart ? resolvePath(payload, s.chart.metricKey) : undefined;
        return (
          <section key={`${s.title}-${i}`} data-testid="report-section"
                   style={{ ...S.card, marginBottom: 12 }}>
            <div style={{ ...S.cardHead, marginBottom: 8 }}>
              {/* ⭐ THE NUMBER IS OURS. The model no longer types one and the
                  parser strips it if it does, so this index is the only
                  numbering that exists and it cannot disagree with the order. */}
              <h3 style={{ ...S.h2, fontSize: 13 }}>
                <span style={{ color: C.faint, marginRight: 6 }}>{i + 1}.</span>
                {s.title}
              </h3>
              <StatusPill status={s.status} />
            </div>
            {s.bullets.length ? (
              <ul style={{ margin: '0 0 2px', paddingLeft: 18 }}>
                {s.bullets.map((b, k) => (
                  <li key={k} style={{ fontSize: 13, lineHeight: 1.6, marginBottom: 4 }}>
                    {emphasise(b)}
                  </li>
                ))}
              </ul>
            ) : null}
            {draw ? (
              <div className="ceo-chart-well" data-testid={`report-chart-${s.chart.type}`}
                   style={{ marginTop: 10, minWidth: 0 }}>
                {draw(data, payload)}
              </div>
            ) : null}
          </section>
        );
      })}

      {/* ── Risks ──────────────────────────────────────────────────────── */}
      {report.risks.length ? (
        <section style={{ ...S.card, marginBottom: 12 }} data-testid="report-risks">
          <h3 style={{ ...S.h2, fontSize: 13, marginBottom: 8 }}>Risks</h3>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {report.risks.map((r, i) => (
              <li key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline',
                                   fontSize: 13, lineHeight: 1.6, marginBottom: 5 }}>
                {/* ⚠ The dot is an ACCENT, not the message: severity is also
                    written out, because colour alone is unreadable in print,
                    in the other theme, and for a colour-blind reader. */}
                <span aria-hidden="true" style={{ flex: '0 0 auto', width: 8, height: 8, marginTop: 5,
                             borderRadius: 999, background: RISK_DOT[r.severity] || C.muted }} />
                <span>
                  {r.severity ? (
                    <strong style={{ color: RISK_DOT[r.severity] || C.muted, marginRight: 6,
                                     fontSize: 11, textTransform: 'uppercase' }}>
                      {r.severity}
                    </strong>
                  ) : null}
                  {emphasise(r.text)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* ── Recommended actions ────────────────────────────────────────── */}
      {report.actions.length ? (
        <section style={{ ...S.card, marginBottom: 12 }} data-testid="report-actions">
          <h3 style={{ ...S.h2, fontSize: 13, marginBottom: 8 }}>Recommended actions</h3>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {report.actions.map((a, i) => (
              <li key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline',
                                   fontSize: 13, lineHeight: 1.6, marginBottom: 6 }}>
                {/* ⚠ A CHECKLIST GLYPH, NOT AN <input type=checkbox>. Nothing
                    stores a tick, and a control that forgets what you did to
                    it is worse than no control. */}
                <span aria-hidden="true" style={{ color: C.faint }}>☐</span>
                <span>
                  {emphasise(a.text)}
                  {a.owner || a.due ? (
                    <span style={{ color: C.faint, fontSize: 11 }}>
                      {' '}— {[a.owner, a.due].filter(Boolean).join(', ')}
                    </span>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* ── Charts the server refused ──────────────────────────────────── */}
      {droppedCharts && droppedCharts.length ? (
        /*
         * ⛔⛔ SHOWN, NOT SWALLOWED. The model asked for a chart and the server
         * refused it because its metricKey did not resolve in the payload.
         * Hiding that would make a missing chart indistinguishable from a
         * chart nobody asked for — and the reason is usually a real finding
         * (a probe that failed, a block that is not tracked).
         */
        <div data-testid="report-dropped-charts"
             style={{ fontSize: 11, color: C.muted, lineHeight: 1.6, marginTop: 4 }}>
          ⚠ {droppedCharts.length} chart request(s) were not drawn because the figures
          behind them are not in this report's payload:{' '}
          {droppedCharts.map((d) => `${d.type} (${d.reason})`).join('; ')}
        </div>
      ) : null}
    </article>
  );
}
